import { DefaultSort, SortDirection } from './sort-utils';
import {TableColumn} from '../../../shared';

/**
 * Helper para configurar rápidamente el ordenamiento en cualquier componente
 */
export class SortHelper {

  /**
   * Configura las funciones de ordenamiento para todas las columnas
   */
  static configureSortFunctions(
    columns: TableColumn[],
    onSort: (column: TableColumn) => void
  ): void {
    columns.forEach(column => {
      if (this.shouldEnableSort(column)) {
        column.sortable = true;
        column.sortFn = () => onSort(column);
        column.sortDirection = null;
      }
    });
  }

  /**
   * Reconfigura las funciones de ordenamiento para columnas específicas
   * Útil cuando las optionList se cargan de forma asíncrona
   */
  static reconfigureSortForColumn(
    column: TableColumn,
    onSort: (column: TableColumn) => void
  ): void {
    if (this.shouldEnableSort(column)) {
      column.sortable = true;
      column.sortFn = () => onSort(column);
      // Mantener la dirección actual si ya estaba configurada
      if (column.sortDirection === undefined) {
        column.sortDirection = null;
      }
    }
  }

  /**
   * Configura el ordenamiento cuando se actualiza una optionList
   * Versión simplificada para usar en suscripciones
   */
  static setupSortWhenOptionListReady(
    columns: TableColumn[],
    columnName: string,
    optionList: any[],
    onSort: (column: TableColumn) => void
  ): void {
    const column = columns.find(col => col.name === columnName);
    if (column && optionList && optionList.length > 0) {
      column.optionList = optionList;
      this.reconfigureSortForColumn(column, onSort);
    }
  }

  /**
   * Determina si una columna debe tener ordenamiento habilitado
   */
  static shouldEnableSort(column: TableColumn): boolean {
    // Tipos básicos que se pueden ordenar
    const basicSortableTypes = ['text', 'number', 'date', 'boolean'];
    
    // Tipos que NO se pueden ordenar
    const nonSortableTypes = ['multi-select', 'money-rate'];
    
    if (!column.pathData) return false;
    
    // Si es un tipo no ordenable, retornar false
    if (nonSortableTypes.includes(column.pathData.type)) return false;
    
    // Para tipos básicos, verificar que estén en la lista
    if (basicSortableTypes.includes(column.pathData.type)) return true;
    
    // Para selects y avatar-selects, verificar que tengan nestedPath y optionList
    if (column.pathData.type === 'select' || column.pathData.type === 'avatar-select') {
      return !!(column.pathData.nestedPath && column.optionList && column.optionList.length > 0);
    }
    
    return false;
  }

  /**
   * Enciende el orden por defecto en su columna y la devuelve para registrarla como orden activo.
   * Sin orden por defecto, o si la columna no existe o no se puede ordenar, no toca nada.
   */
  static applyDefaultSort(
    columns: TableColumn[],
    defaultSort?: DefaultSort
  ): TableColumn | undefined {
    if (!defaultSort) return undefined;

    const column = columns.find(col => col.pathData?.name === defaultSort.columnName);
    if (!column?.sortable) return undefined;

    this.resetOtherColumns(columns, column);
    column.sortDirection = defaultSort.direction;
    return column;
  }

  /**
   * Resetea el ordenamiento de todas las columnas excepto la especificada
   */
  static resetOtherColumns(columns: TableColumn[], activeColumn: TableColumn): void {
    columns.forEach(column => {
      if (column !== activeColumn) {
        column.sortDirection = null;
      }
    });
  }

  /**
   * Obtiene la dirección de ordenamiento de una columna
   */
  static getColumnSortDirection(column: TableColumn): SortDirection {
    return column.sortDirection || null;
  }

  /**
   * Configuración rápida para tipos de datos comunes
   */
  static getDefaultSortConfig(): Record<string, any> {
    return {
      text: {
        caseSensitive: false,
        nullsLast: true
      },
      number: {
        nullsLast: true
      },
      date: {
        nullsLast: true
      },
      boolean: {
        nullsLast: true
      }
    };
  }
}
